import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut } from "lucide-react";
import AboutEditor from "../components/AboutEditor";

const BODY_TYPES = ["Sedan", "SUV", "Coupe", "Convertible"];
const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const TRANS = ["Automatic", "Manual"];

const emptyCar = {
  brand: "", model: "", year: 2022, price: 0, mileage: 0,
  fuel: "Petrol", transmission: "Automatic", body_type: "Sedan",
  color: "", location: "", description: "",
  features: "", images: "",
  featured: false, sold: false,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [cars, setCars] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [sellRequests, setSellRequests] = useState([]);

  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const [form, setForm] = useState(emptyCar);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, i, s] = await Promise.all([
        api.get("/cars", { params: { include_sold: true } }),
        api.get("/admin/inquiries"),
        api.get("/admin/sell-requests"),
      ]);
      setCars(c.data);
      setInquiries(i.data);
      setSellRequests(s.data);
    } catch {
      toast.error("Session expired. Please sign in again.");
      localStorage.removeItem("ry_admin_token");
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("ry_admin_token");
    if (!token) { navigate("/admin/login"); return; }
    api.get("/admin/me")
      .then(() => { setAuthed(true); loadAll(); })
      .catch(() => {
        localStorage.removeItem("ry_admin_token");
        navigate("/admin/login");
      });
  }, [navigate, loadAll]);

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch {}
    localStorage.removeItem("ry_admin_token");
    navigate("/admin/login");
  };

  const openNew = () => { setForm(emptyCar); setEditing({}); };
  const openEdit = (car) => {
    setForm({
      ...car,
      features: (car.features || []).join(", "),
      images: (car.images || []).join("\n"),
    });
    setEditing(car);
  };

  const saveCar = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year),
      price: Number(form.price),
      mileage: Number(form.mileage),
      features: typeof form.features === "string"
        ? form.features.split(",").map((s) => s.trim()).filter(Boolean)
        : form.features,
      images: typeof form.images === "string"
        ? form.images.split("\n").map((s) => s.trim()).filter(Boolean)
        : form.images,
    };
    try {
      if (editing && editing.id) {
        await api.put(`/admin/cars/${editing.id}`, payload);
        toast.success("Car updated.");
      } else {
        await api.post("/admin/cars", payload);
        toast.success("Car added.");
      }
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCar = async (car) => {
    if (!window.confirm(`Delete ${car.brand} ${car.model}?`)) return;
    try {
      await api.delete(`/admin/cars/${car.id}`);
      toast.success("Deleted.");
      loadAll();
    } catch { toast.error("Could not delete."); }
  };

  const deleteInquiry = async (i) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${i.id}`);
    loadAll();
  };

  const deleteSell = async (i) => {
    if (!window.confirm("Delete this sell request?")) return;
    await api.delete(`/admin/sell-requests/${i.id}`);
    loadAll();
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FCFCFC]" data-testid="admin-dashboard-page">
      <header className="bg-white border-b border-gray-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-tight">
            ROYALE <span className="text-[#9D8C70] font-light">YAAN</span> · Admin
          </Link>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout" className="text-xs gap-2">
            <LogOut size={14} /> Sign out
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-10">
        <Tabs defaultValue="cars" className="w-full">
          <TabsList className="bg-white border border-gray-200 rounded-sm p-1 h-auto">
            <TabsTrigger value="cars" data-testid="tab-cars" className="rounded-none px-6 py-2 text-sm">Cars ({cars.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-none px-6 py-2 text-sm">Inquiries ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="sell" data-testid="tab-sell" className="rounded-none px-6 py-2 text-sm">Sell Requests ({sellRequests.length})</TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about" className="rounded-none px-6 py-2 text-sm">About Page</TabsTrigger>
          </TabsList>

          {/* Cars */}
          <TabsContent value="cars" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl tracking-tight">Inventory</h2>
              <Button onClick={openNew} data-testid="admin-add-car" className="btn-primary rounded-sm gap-2">
                <Plus size={15} /> Add Car
              </Button>
            </div>
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F8F8] text-left">
                  <tr>
                    <Th>Car</Th><Th>Year</Th><Th>Price</Th><Th>Status</Th><Th>Featured</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100" data-testid={`admin-car-row-${c.id}`}>
                      <td className="px-5 py-4">
                        <p className="font-medium">{c.brand} {c.model}</p>
                        <p className="text-xs text-gray-500">{c.location} · {c.color}</p>
                      </td>
                      <td className="px-5 py-4">{c.year}</td>
                      <td className="px-5 py-4">{formatINR(c.price)}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-1 ${c.sold ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
                          {c.sold ? "Sold" : "Available"}
                        </span>
                      </td>
                      <td className="px-5 py-4">{c.featured ? "Yes" : "—"}</td>
                      <td className="px-5 py-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`admin-edit-${c.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCar(c)} data-testid={`admin-delete-${c.id}`}><Trash2 size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                  {cars.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-16 text-center text-gray-500">No cars yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Inquiries */}
          <TabsContent value="inquiries" className="mt-6">
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F8F8] text-left">
                  <tr><Th>When</Th><Th>Name</Th><Th>Contact</Th><Th>Car</Th><Th>Message</Th><Th></Th></tr>
                </thead>
                <tbody>
                  {inquiries.map((i) => (
                    <tr key={i.id} className="border-t border-gray-100" data-testid={`admin-inquiry-${i.id}`}>
                      <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">{new Date(i.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4 font-medium">{i.name}</td>
                      <td className="px-5 py-4">
                        <p className="text-xs">{i.email}</p>
                        <p className="text-xs text-gray-500">{i.phone}</p>
                      </td>
                      <td className="px-5 py-4 text-xs">{i.car_title || "General"}</td>
                      <td className="px-5 py-4 text-xs max-w-md">{i.message}</td>
                      <td className="px-5 py-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => deleteInquiry(i)}><Trash2 size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                  {inquiries.length === 0 && <tr><td colSpan={6} className="px-5 py-16 text-center text-gray-500">No inquiries yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Sell Requests */}
          <TabsContent value="sell" className="mt-6">
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F8F8] text-left">
                  <tr><Th>When</Th><Th>Name</Th><Th>Contact</Th><Th>Vehicle</Th><Th>Expected</Th><Th></Th></tr>
                </thead>
                <tbody>
                  {sellRequests.map((s) => (
                    <tr key={s.id} className="border-t border-gray-100" data-testid={`admin-sell-${s.id}`}>
                      <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4 font-medium">{s.name}</td>
                      <td className="px-5 py-4">
                        <p className="text-xs">{s.email}</p>
                        <p className="text-xs text-gray-500">{s.phone}</p>
                      </td>
                      <td className="px-5 py-4 text-xs">
                        <p>{s.year} {s.brand} {s.model}</p>
                        <p className="text-gray-500">{Number(s.mileage).toLocaleString("en-IN")} km · {s.fuel}</p>
                      </td>
                      <td className="px-5 py-4 text-xs">{s.expected_price ? formatINR(s.expected_price) : "—"}</td>
                      <td className="px-5 py-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => deleteSell(s)}><Trash2 size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                  {sellRequests.length === 0 && <tr><td colSpan={6} className="px-5 py-16 text-center text-gray-500">No sell requests yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* About page editor */}
          <TabsContent value="about" className="mt-6">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h2 className="font-display text-2xl tracking-tight">About page</h2>
                <p className="text-xs text-gray-500 mt-1">Edits go live immediately on the public /about page.</p>
              </div>
            </div>
            <AboutEditor />
          </TabsContent>
        </Tabs>
      </div>

      {/* Editor dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-car-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">{editing?.id ? "Edit car" : "Add car"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveCar} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Brand"><Input data-testid="form-brand" value={form.brand} onChange={(e)=>setForm({...form,brand:e.target.value})} required /></F>
              <F label="Model"><Input data-testid="form-model" value={form.model} onChange={(e)=>setForm({...form,model:e.target.value})} required /></F>
              <F label="Year"><Input type="number" data-testid="form-year" value={form.year} onChange={(e)=>setForm({...form,year:e.target.value})} required /></F>
              <F label="Price (₹)"><Input type="number" data-testid="form-price" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})} required /></F>
              <F label="Mileage (km)"><Input type="number" data-testid="form-mileage" value={form.mileage} onChange={(e)=>setForm({...form,mileage:e.target.value})} required /></F>
              <F label="Color"><Input data-testid="form-color" value={form.color} onChange={(e)=>setForm({...form,color:e.target.value})} /></F>
              <F label="Body type">
                <Select value={form.body_type} onValueChange={(v)=>setForm({...form,body_type:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BODY_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Fuel">
                <Select value={form.fuel} onValueChange={(v)=>setForm({...form,fuel:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FUELS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Transmission">
                <Select value={form.transmission} onValueChange={(v)=>setForm({...form,transmission:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TRANS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Location"><Input data-testid="form-location" value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} /></F>
            </div>
            <F label="Description">
              <Textarea rows={3} data-testid="form-description" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} />
            </F>
            <F label="Features (comma-separated)">
              <Input data-testid="form-features" value={form.features} onChange={(e)=>setForm({...form,features:e.target.value})} />
            </F>
            <F label="Image URLs (one per line)">
              <Textarea rows={3} data-testid="form-images" value={form.images} onChange={(e)=>setForm({...form,images:e.target.value})} />
            </F>
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <Switch checked={form.featured} onCheckedChange={(v)=>setForm({...form,featured:v})} data-testid="form-featured" />
                <Label className="text-sm">Featured</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.sold} onCheckedChange={(v)=>setForm({...form,sold:v})} data-testid="form-sold" />
                <Label className="text-sm">Sold</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setEditing(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="form-save" className="btn-primary rounded-sm">
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] text-gray-500 font-medium">{children}</th>;

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.15em] text-gray-600">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
